/**
 * 의약품 정보 생성 — 레거시 `drugInfoCreate.js` / Figma 카드형 UI 포팅.
 * 년월 · 동기화/워크플로 카드 · 확인필요·공개 · 검색필터가 분리된 레이아웃.
 */
import type {
  DataCollectionTargetTable,
  DrugDataFlags,
  DrugDataResetTarget,
} from '@/entities/drug-data/api/drug-data.api';
import {
  drugDataService,
  isDrugDataAlreadyReleasedError,
  isDrugDataProcessRunningError,
} from '@/entities/drug-data/api/drug-data.api';
import {
  useCancelDrugDataRelease,
  useCreateCodeMapping,
  useDrugDataDrugList,
  useDrugDataFlags,
  useDrugDataReleaseStatus,
  useMergeHiraDrug,
  useProcessHiraNonBenefit,
  useReleaseDrugData,
  useResetDrugData,
  useRunDataCollectionJob,
  useSetDrugDataFlags,
  useUpdateDrugInfo,
  useValidateCommissionDrugCodes,
} from '@/entities/drug-data/hooks/useDrugData';
import {
  isDrugDataRequestTimeout,
  useDrugDataFlagPolling,
  type DrugDataFlagTaskKey,
} from '@/entities/drug-data/hooks/useDrugDataFlagPolling';
import { ConfirmNeededDrugsModal } from '@/pages/drugs/ConfirmNeededDrugsModal';
import { DataCollectionTableSelectModal } from '@/pages/drugs/DataCollectionTableSelectModal';
import { DrugDataResetModal } from '@/pages/drugs/DrugDataResetModal';
import { DrugInfoPublishScheduleModal } from '@/pages/drugs/DrugInfoPublishScheduleModal';
import { DrugInfoUpdateConfirmModal } from '@/pages/drugs/DrugInfoUpdateConfirmModal';
import { getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { MonthPicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { SimplePagination } from '@/shared/components/SimplePagination';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Separator } from '@/shared/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { useListPagination } from '@/shared/hooks/useListPagination';
import { cn } from '@/shared/lib/utils';
import { SPREADSHEET_UPLOAD_ACCEPT } from '@/shared/utils/csvImport';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  AlertCircle,
  Check,
  DownloadCloud,
  Loader2,
  MinusCircle,
  RotateCcw,
  Upload,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const INFO_CATEGORY_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'API', label: 'API' },
  { value: 'DUR', label: 'DUR' },
  { value: 'ADMIN', label: 'ADMIN' },
] as const;

const INFO_FIELD_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'drugCode', label: '의약품 코드' },
  { value: 'drugCodePharma', label: '제약사 약품 코드' },
  { value: 'drugCodeMfds', label: '품목 기준 코드' },
  { value: 'drugName', label: '의약품명' },
  { value: 'pharmaCompanyName', label: '제약사명' },
] as const;

const WORKFLOW_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  SUCCESS: 'success',
  ERROR: 'error',
} as const;

type WorkflowStatus = (typeof WORKFLOW_STATUS)[keyof typeof WORKFLOW_STATUS];

/** 폴링 완료·만료 안내에 쓰는 작업 이름 */
const DRUG_DATA_TASK_LABELS: Record<DrugDataFlagTaskKey, string> = {
  batchFlag: '약가기준정보조회서비스',
  mergeFlag: '의약품 기초 정보 생성',
  updateFlag: '의약품 정보 업데이트',
  durFlag: 'DUR점검대상비급여의약품',
  codeMappingDone: '의약품코드맵핑정보',
};

/** 버튼(ruda primary)과 동일한 브랜드 색 */
const BRAND_PRIMARY = '#4152cf';

const STATUS_ICON_COLORS: Record<WorkflowStatus, string> = {
  pending: '#8c8c8c',
  running: BRAND_PRIMARY,
  success: BRAND_PRIMARY,
  error: '#ff4d4f',
};

const TIMESTAMP_PILL_BORDER: Record<WorkflowStatus, string> = {
  pending: '#dfeaf2',
  running: '#a2b6ee',
  success: BRAND_PRIMARY,
  error: '#ffccc7',
};

const TIMESTAMP_PILL_TEXT: Record<WorkflowStatus, string> = {
  pending: '#718ebf',
  running: BRAND_PRIMARY,
  success: BRAND_PRIMARY,
  error: '#ff4d4f',
};

const TIMESTAMP_PILL_BG: Record<WorkflowStatus, string> = {
  pending: 'transparent',
  running: '#f2f4fd',
  success: '#e0e6f9',
  error: '#fff1f0',
};

const SYNC_COLUMNS = [
  {
    id: 'priceService',
    title: '약가기준정보조회서비스',
    showUpdate: true,
    showUpload: false,
  },
  {
    id: 'dur',
    title: 'DUR점검대상비급여의약품',
    showUpdate: false,
    showUpload: true,
  },
  {
    id: 'mapping',
    title: '의약품코드맵핑정보',
    showUpdate: true,
    showUpload: true,
  },
  {
    id: 'settlementFee',
    title: '정산수수료 정보',
    showUpdate: false,
    showUpload: false,
  },
] as const;

const SYNC_FLAG_KEYS = {
  priceService: { flag: 'batchFlag', updatedAt: 'batchFlagUpdatedAt' },
  dur: { flag: 'durFlag', updatedAt: 'durFlagUpdatedAt' },
  mapping: { flag: 'codeMappingFlag', updatedAt: 'codeMappingFlagUpdatedAt' },
  settlementFee: {
    flag: 'commissionFlag',
    updatedAt: 'commissionFlagUpdatedAt',
  },
} as const;

const WORKFLOW_CARDS = [
  {
    id: 'baseCreate',
    title: '의약품 기초 정보 생성',
    actionLabel: '생성하기',
  },
  {
    id: 'manual',
    title: '의약품 정보 수기 입력',
    actionLabel: '입력완료',
  },
  {
    id: 'drugUpdate',
    title: '의약품 정보 업데이트',
    actionLabel: '업데이트',
  },
] as const;

const formatPillTime = (v?: string | null): string => {
  if (v == null || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const workflowStatusFromFlag = (flag?: boolean | null): WorkflowStatus =>
  flag === true ? WORKFLOW_STATUS.SUCCESS : WORKFLOW_STATUS.PENDING;

const readFlag = (flags: DrugDataFlags | undefined, key: string) => {
  if (!flags) return undefined;
  return (flags as unknown as Record<string, unknown>)[key] as
    | boolean
    | string
    | undefined;
};

const WorkflowStatusIcon: React.FC<{ status: WorkflowStatus }> = ({
  status,
}) => {
  const color = STATUS_ICON_COLORS[status];
  if (status === WORKFLOW_STATUS.RUNNING) {
    return <Loader2 className="size-8 animate-spin" style={{ color }} />;
  }
  // 업데이트 완료 — 버튼과 동일 primary 원형 체크
  if (status === WORKFLOW_STATUS.SUCCESS) {
    return (
      <span
        className="inline-flex size-8 items-center justify-center rounded-full text-white shadow-sm"
        style={{ backgroundColor: BRAND_PRIMARY }}
        aria-label="완료"
      >
        <Check className="size-4" strokeWidth={3} />
      </span>
    );
  }
  if (status === WORKFLOW_STATUS.ERROR) {
    return <AlertCircle className="size-8" style={{ color }} />;
  }
  return <MinusCircle className="size-8" style={{ color }} />;
};

const TimestampPill: React.FC<{
  children: React.ReactNode;
  muted?: boolean;
  status?: WorkflowStatus;
}> = ({ children, muted, status = WORKFLOW_STATUS.PENDING }) => (
  <div
    className="flex min-h-[30px] w-full items-center justify-center rounded-[15px] px-2 py-1.5"
    style={{
      border: `1px solid ${TIMESTAMP_PILL_BORDER[status]}`,
      backgroundColor: TIMESTAMP_PILL_BG[status],
    }}
  >
    <span
      className={cn(
        'text-[10px] leading-none',
        status === WORKFLOW_STATUS.SUCCESS && 'font-semibold',
      )}
      style={{ color: muted ? '#d7d7d7' : TIMESTAMP_PILL_TEXT[status] }}
    >
      {children}
    </span>
  </div>
);

const SyncServiceColumn: React.FC<{
  title: string;
  timeLabel: string;
  status: WorkflowStatus;
  showUpdate?: boolean;
  showUpload?: boolean;
  onUpdate?: () => void;
  onUpload?: () => void;
  updateDisabled?: boolean;
  uploadDisabled?: boolean;
  updateLoading?: boolean;
  uploadLoading?: boolean;
}> = ({
  title,
  timeLabel,
  status,
  showUpdate,
  showUpload,
  onUpdate,
  onUpload,
  updateDisabled,
  uploadDisabled,
  updateLoading,
  uploadLoading,
}) => {
  const muted = timeLabel === '-' || timeLabel === '—';
  return (
    <div className="flex min-w-[140px] flex-1 flex-col items-center gap-3.5 px-1">
      <p className="text-center text-sm text-[#232323]">{title}</p>
      <WorkflowStatusIcon status={status} />
      <TimestampPill muted={muted} status={status}>
        {timeLabel}
      </TimestampPill>
      <div className="flex min-h-8 items-center gap-3.5">
        {showUpdate ? (
          <MenuWriteGate>
            <Button
              type="button"
              size="sm"
              className="ruda-button h-8 w-20"
              disabled={!onUpdate || updateDisabled || updateLoading}
              onClick={onUpdate}
            >
              {updateLoading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                '업데이트'
              )}
            </Button>
          </MenuWriteGate>
        ) : null}
        {showUpload ? (
          <MenuWriteGate>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground"
              disabled={!onUpload || uploadDisabled || uploadLoading}
              onClick={onUpload}
            >
              {uploadLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
            </Button>
          </MenuWriteGate>
        ) : null}
      </div>
    </div>
  );
};

const ActionWorkflowCard: React.FC<{
  title: string;
  actionLabel: string;
  status: WorkflowStatus;
  timeLabel: string;
  borderActive?: boolean;
  actionDisabled?: boolean;
  actionLoading?: boolean;
  onAction?: () => void;
}> = ({
  title,
  actionLabel,
  status,
  timeLabel,
  borderActive,
  actionDisabled,
  actionLoading,
  onAction,
}) => (
  <div
    className={cn(
      'flex h-full flex-col items-center gap-3.5 rounded-[15px] bg-white p-5',
      borderActive
        ? 'border-[1.5px] border-[#6d6d6d]'
        : 'border border-[#d0d0d0]',
    )}
  >
    <p className="max-w-[160px] text-center text-sm text-[#232323]">{title}</p>
    <WorkflowStatusIcon status={status} />
    <TimestampPill
      muted={timeLabel === '-' || timeLabel === '—'}
      status={status}
    >
      {timeLabel}
    </TimestampPill>
    <MenuWriteGate>
      <Button
        type="button"
        size="sm"
        className="ruda-button h-8 w-20"
        disabled={actionDisabled || actionLoading}
        onClick={onAction}
      >
        {actionLoading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          actionLabel
        )}
      </Button>
    </MenuWriteGate>
  </div>
);

export const DrugDataCreatePage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const routeSearch = useSearch({ strict: false }) as { refYm?: string };
  const initialYm =
    routeSearch.refYm && /^\d{6}$/.test(routeSearch.refYm)
      ? routeSearch.refYm
      : getCurrentRefYm();

  const [baseYm, setBaseYm] = useState(initialYm);
  const [infoCategory, setInfoCategory] = useState('all');
  const [infoField, setInfoField] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [underContractOnly, setUnderContractOnly] = useState(false);
  const [search, setSearch] = useState<{
    refYm: string;
    drugCode?: string;
    drugCodePharma?: string;
    drugCodeMfds?: string;
    drugName?: string;
    pharmaCompanyName?: string;
    /** 거래중 필터 — API contractStatus=ACTIVE */
    contractStatus?: string;
    dataSourceType?: string;
  }>({ refYm: initialYm });

  const { page, setPage, pageSize, setPageSize, pageable } =
    useListPagination();

  const {
    data: flags,
    isLoading: flagsLoading,
    isError: flagsError,
    refetch: refetchFlags,
  } = useDrugDataFlags(baseYm);
  const { data: releaseStatus, refetch: refetchRelease } =
    useDrugDataReleaseStatus(baseYm);
  const { data, isLoading, isError, error, isFetching } = useDrugDataDrugList(
    search,
    pageable,
  );

  const releaseDrugData = useReleaseDrugData();
  const cancelRelease = useCancelDrugDataRelease();
  const resetDrugData = useResetDrugData();
  const runBatch = useRunDataCollectionJob();
  const mergeHira = useMergeHiraDrug();
  const updateDrugInfo = useUpdateDrugInfo();
  const processHiraNonBenefit = useProcessHiraNonBenefit();
  const createCodeMapping = useCreateCodeMapping();
  const setDrugDataFlags = useSetDrugDataFlags();
  const validateCommission = useValidateCommissionDrugCodes();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [updateConfirmOpen, setUpdateConfirmOpen] = useState(false);
  const [tableSelectOpen, setTableSelectOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [missingCount, setMissingCount] = useState<number | null>(null);

  const fetchFlags = useCallback(
    () => drugDataService.getFlags(baseYm),
    [baseYm],
  );

  const onPollingComplete = useCallback(
    (flagKey: DrugDataFlagTaskKey) => {
      void refetchFlags();
      void refetchRelease();
      toast.success(`${DRUG_DATA_TASK_LABELS[flagKey]} 처리가 완료되었습니다.`);
    },
    [refetchFlags, refetchRelease, toast],
  );

  // 서버 flags 에 실패 상태가 없어, 오래 기다려도 완료가 안 오면 대기를 멈추고 확인을 안내한다
  const onPollingExpire = useCallback(
    (flagKey: DrugDataFlagTaskKey) => {
      void refetchFlags();
      toast.warning(
        `${DRUG_DATA_TASK_LABELS[flagKey]} 처리 완료를 1시간 동안 확인하지 못해 대기를 멈췄습니다. 처리 결과를 확인해 주세요.`,
      );
    },
    [refetchFlags, toast],
  );

  const { beginTask, settleTask, cancelTask, isTaskPending, pendingKeys } =
    useDrugDataFlagPolling(
      baseYm,
      fetchFlags,
      onPollingComplete,
      onPollingExpire,
    );

  const runFlagTask = useCallback(
    async (
      flagKey: DrugDataFlagTaskKey,
      run: () => Promise<unknown>,
      requestMessage: string,
    ) => {
      if (isTaskPending(flagKey)) return;
      beginTask(flagKey, flags);
      toast.success(requestMessage);
      try {
        await run();
        await settleTask(flagKey);
        void refetchFlags();
      } catch (err) {
        if (isDrugDataRequestTimeout(err)) {
          return;
        }
        cancelTask(flagKey);
        handleApiError(err, toast);
      }
    },
    [
      beginTask,
      cancelTask,
      flags,
      isTaskPending,
      refetchFlags,
      settleTask,
      toast,
    ],
  );

  const isFlagPending = useCallback(
    (key: string) => {
      if (key === 'codeMappingFlag') {
        return pendingKeys.includes('codeMappingDone');
      }
      return pendingKeys.includes(key);
    },
    [pendingKeys],
  );

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const refreshMissingCount = useCallback(async () => {
    if (!baseYm || !flags?.mergeFlag) {
      setMissingCount(null);
      return;
    }
    try {
      const result = await validateCommission.mutateAsync(baseYm);
      const n = result.totalMissingCount ?? result.missingCount;
      setMissingCount(typeof n === 'number' ? n : null);
    } catch {
      setMissingCount(null);
    }
  }, [baseYm, flags?.mergeFlag, validateCommission]);

  useEffect(() => {
    void refreshMissingCount();
  }, [baseYm, flags?.mergeFlag]); // eslint-disable-line react-hooks/exhaustive-deps

  const leftPrerequisitesSatisfied = useMemo(() => {
    if (!flags || flagsError) return false;
    if (typeof flags.leftPrerequisitesSatisfied === 'boolean') {
      return flags.leftPrerequisitesSatisfied;
    }
    const mappingDone =
      flags.codeMappingFlag === true || flags.codeMappingDone === true;
    const commissionDone =
      flags.commissionFlag === true || flags.commissionUploaded === true;
    return (
      flags.batchFlag === true &&
      flags.durFlag === true &&
      mappingDone &&
      commissionDone
    );
  }, [flags, flagsError]);

  const manualInputEnabled = useMemo(() => {
    if (!flags || flagsError || flags.mergeFlag !== true) return false;
    return missingCount === null || missingCount === 0;
  }, [flags, flagsError, missingCount]);

  const drugUpdateEnabled = useMemo(() => {
    if (!flags || flagsError) return false;
    return flags.manualFlag === true;
  }, [flags, flagsError]);

  const syncServiceUi = useMemo(() => {
    const ids = Object.keys(SYNC_FLAG_KEYS) as Array<
      keyof typeof SYNC_FLAG_KEYS
    >;
    const map: Record<string, { status: WorkflowStatus; timeLabel: string }> =
      {};
    for (const id of ids) {
      const { flag: fk, updatedAt: tk } = SYNC_FLAG_KEYS[id];
      if (isFlagPending(fk)) {
        map[id] = { status: WORKFLOW_STATUS.RUNNING, timeLabel: '처리 중…' };
        continue;
      }
      if (flagsError) {
        map[id] = { status: WORKFLOW_STATUS.ERROR, timeLabel: '—' };
        continue;
      }
      if (!flags || flagsLoading) {
        map[id] = { status: WORKFLOW_STATUS.PENDING, timeLabel: '—' };
        continue;
      }
      const flagVal =
        fk === 'codeMappingFlag'
          ? (flags.codeMappingFlag ?? flags.codeMappingDone)
          : fk === 'commissionFlag'
            ? (flags.commissionFlag ?? flags.commissionUploaded)
            : readFlag(flags, fk);
      const at =
        fk === 'commissionFlag'
          ? flags.commissionFlagUpdatedAt
          : (readFlag(flags, tk) as string | undefined);
      map[id] = {
        status: workflowStatusFromFlag(flagVal === true),
        timeLabel: at ? formatPillTime(at) : '—',
      };
    }
    return map;
  }, [flags, flagsError, flagsLoading, isFlagPending]);

  const workflowCardUi = useMemo(() => {
    const pending = { status: WORKFLOW_STATUS.PENDING, timeLabel: '—' };
    const err = { status: WORKFLOW_STATUS.ERROR, timeLabel: '—' };
    const mk = (flagKey: string, atKey: string) => {
      if (isFlagPending(flagKey)) {
        return { status: WORKFLOW_STATUS.RUNNING, timeLabel: '처리 중…' };
      }
      if (flagsError) return err;
      if (!flags || flagsLoading) return pending;
      const f = readFlag(flags, flagKey);
      const at = readFlag(flags, atKey) as string | undefined;
      return {
        status: workflowStatusFromFlag(f === true),
        timeLabel: at ? formatPillTime(at) : '—',
      };
    };
    return {
      baseCreate: mk('mergeFlag', 'mergeFlagUpdatedAt'),
      manual: flagsError
        ? err
        : !flags || flagsLoading
          ? pending
          : {
              status: workflowStatusFromFlag(flags.manualFlag === true),
              timeLabel: flags.manualFlagUpdatedAt
                ? formatPillTime(flags.manualFlagUpdatedAt)
                : '—',
            },
      drugUpdate: mk('updateFlag', 'updateFlagUpdatedAt'),
    };
  }, [flags, flagsError, flagsLoading, isFlagPending]);

  const workflowCardsWithUi = useMemo(() => {
    return WORKFLOW_CARDS.map((card, index) => {
      const ui = workflowCardUi[card.id];
      let prevChain = leftPrerequisitesSatisfied;
      for (let i = 0; i < index; i++) {
        const prevUi = workflowCardUi[WORKFLOW_CARDS[i].id];
        prevChain = prevChain && prevUi.status === WORKFLOW_STATUS.SUCCESS;
      }
      const borderActive =
        prevChain &&
        ui.status !== WORKFLOW_STATUS.SUCCESS &&
        ui.status !== WORKFLOW_STATUS.RUNNING;
      return { ...card, ...ui, borderActive };
    });
  }, [leftPrerequisitesSatisfied, workflowCardUi]);

  const releaseWorkflowStatus: WorkflowStatus = releaseStatus?.released
    ? WORKFLOW_STATUS.SUCCESS
    : WORKFLOW_STATUS.PENDING;
  const releaseTimeLabel = releaseStatus?.releasedAt
    ? formatPillTime(releaseStatus.releasedAt)
    : '—';

  const rows = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handleBaseYmChange = (next: string) => {
    const ym = next || getCurrentRefYm();
    setBaseYm(ym);
    // 새로고침·링크 공유 시 같은 년월로 열리도록 주소창 ?refYm= 도 바꾼다(히스토리는 쌓지 않음)
    navigate({ search: { refYm: ym }, replace: true } as never);
    // 년월 변경 시 검색 필터·목록 조건 전부 초기화
    setInfoCategory('all');
    setInfoField('all');
    setSearchText('');
    setUnderContractOnly(false);
    setSearch({ refYm: ym });
    setPage(1);
    setMissingCount(null);
  };

  const applySearch = useCallback(
    (
      overrides?: Partial<typeof search> & { text?: string; field?: string },
    ) => {
      const field = overrides?.field ?? infoField;
      const text = (overrides?.text ?? searchText).trim();
      const contractStatus =
        overrides?.contractStatus !== undefined
          ? overrides.contractStatus
          : underContractOnly
            ? 'ACTIVE'
            : undefined;
      const next: typeof search = {
        refYm: overrides?.refYm ?? baseYm,
        contractStatus,
        dataSourceType:
          overrides?.dataSourceType !== undefined
            ? overrides.dataSourceType
            : infoCategory !== 'all'
              ? infoCategory
              : undefined,
      };
      if (text && field !== 'all') {
        next[field as keyof typeof next] = text as never;
      }
      setSearch(next);
      setPage(1);
    },
    [baseYm, infoCategory, infoField, searchText, underContractOnly],
  );

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    applySearch();
  };

  const pipelineBusy =
    pendingKeys.length > 0 ||
    runBatch.isPending ||
    processHiraNonBenefit.isPending ||
    createCodeMapping.isPending ||
    mergeHira.isPending ||
    updateDrugInfo.isPending ||
    resetDrugData.isPending;

  const handlePriceServiceUpdate = async () => {
    if (
      pipelineBusy ||
      syncServiceUi.priceService?.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    await runFlagTask(
      'batchFlag',
      () => runBatch.mutateAsync({ baseYm }),
      '약가기준정보조회서비스(데이터 수집 배치) 실행을 요청했습니다.',
    );
  };

  /**
   * 원천 데이터 수집(테이블 선택). 전체 선택이면 기존 전체 수집과 같이 배치 완료 플래그를 폴링한다.
   * 일부 선택은 플래그가 바뀌지 않아 폴링하지 않고, 결과는 슬랙 알림으로만 확인할 수 있다.
   * 이미 수집 완료된 달에도 특정 원천만 재수집할 수 있도록 완료 상태와 무관하게 허용한다.
   */
  const handleTableCollection = async (
    targetTables: DataCollectionTargetTable[] | undefined,
  ) => {
    if (pipelineBusy) return;
    if (!targetTables) {
      await runFlagTask(
        'batchFlag',
        () => runBatch.mutateAsync({ baseYm }),
        '약가기준정보조회서비스(데이터 수집 배치) 실행을 요청했습니다.',
      );
      return;
    }
    try {
      await runBatch.mutateAsync({ baseYm, targetTables });
      toast.success(
        '수집 요청이 접수되었습니다. 결과는 슬랙 batch-step-alerts 알림으로 확인하세요.',
      );
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleDurUpload = () => {
    if (
      pipelineBusy ||
      isTaskPending('durFlag') ||
      syncServiceUi.dur?.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = SPREADSHEET_UPLOAD_ACCEPT;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      await runFlagTask(
        'durFlag',
        () => processHiraNonBenefit.mutateAsync({ baseYm, file }),
        'DUR점검대상비급여의약품 처리를 요청했습니다.',
      );
    };
    input.click();
  };

  const handleMappingUpload = () => {
    if (
      pipelineBusy ||
      isTaskPending('codeMappingDone') ||
      syncServiceUi.mapping?.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      await runFlagTask(
        'codeMappingDone',
        () => createCodeMapping.mutateAsync({ baseYm, file }),
        '의약품코드맵핑정보 처리를 요청했습니다.',
      );
    };
    input.click();
  };

  const handleMappingUpdate = async () => {
    if (
      pipelineBusy ||
      syncServiceUi.mapping?.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    await runFlagTask(
      'codeMappingDone',
      () => createCodeMapping.mutateAsync({ baseYm }),
      '의약품코드맵핑정보 업데이트를 요청했습니다.',
    );
  };

  const handleBaseCreate = async () => {
    if (!leftPrerequisitesSatisfied) {
      toast.error(
        '좌측 동기화(약가·DUR·코드맵핑·정산수수료)가 모두 완료되어야 합니다.',
      );
      return;
    }
    if (
      pipelineBusy ||
      workflowCardUi.baseCreate.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    await runFlagTask(
      'mergeFlag',
      () => mergeHira.mutateAsync(baseYm),
      '의약품 기초 정보 생성을 요청했습니다.',
    );
  };

  const handleManualInputComplete = async () => {
    if (workflowCardUi.manual.status === WORKFLOW_STATUS.SUCCESS) return;
    if (!manualInputEnabled) {
      toast.error(
        '기초 정보 생성이 완료되고 확인 필요 의약품이 없을 때만 입력완료할 수 있습니다.',
      );
      return;
    }
    try {
      await setDrugDataFlags.mutateAsync({ baseYm, manualFlag: true });
      toast.success('의약품 정보 수기 입력 완료를 반영했습니다.');
      refetchFlags();
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleUpdateDrugInfo = async () => {
    if (
      pipelineBusy ||
      workflowCardUi.drugUpdate.status === WORKFLOW_STATUS.SUCCESS
    )
      return;
    await runFlagTask(
      'updateFlag',
      () => updateDrugInfo.mutateAsync(baseYm),
      '의약품 정보 업데이트를 요청했습니다.',
    );
  };

  const handleRelease = async (_scheduledAt: string, immediate: boolean) => {
    if (!immediate) {
      toast.info(
        '예약 게시는 아직 지원되지 않습니다. 즉시 게시를 사용해주세요.',
      );
      return;
    }
    try {
      await releaseDrugData.mutateAsync(baseYm);
      toast.success('의약품 데이터 공개를 요청했습니다.');
      refetchRelease();
      refetchFlags();
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  /** 게시(개시) 취소 — 게시된 달을 초기화하려면 먼저 취소해야 한다 */
  const handleCancelRelease = async () => {
    const ok = await confirm({
      title: `${baseYm} 의약품 정보 게시를 취소합니다.`,
      description: [
        '· 앱은 직전에 게시된 기준월 데이터로 돌아갑니다.',
        '· 서버 캐시 때문에 최대 5분 동안 게시 상태로 보일 수 있습니다.',
        '· 완료 단계는 그대로라 바로 다시 게시할 수 있습니다.',
      ].join('\n'),
      confirmLabel: '게시 취소',
      cancelLabel: '닫기',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await cancelRelease.mutateAsync(baseYm);
      toast.success('의약품 정보 게시를 취소했습니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  /**
   * 생성 단계 초기화. 게시된 달·다른 작업 실행 중(409)은 안내만 하고,
   * 그 밖의 실패는 서버가 완료 플래그를 이미 내렸을 수 있어 같은 요청 재시도를 안내한다.
   */
  const handleReset = async (targets: DrugDataResetTarget[] | undefined) => {
    try {
      const result = await resetDrugData.mutateAsync({ baseYm, targets });
      toast.success('의약품 데이터 초기화가 완료되었습니다.');
      return result;
    } catch (err) {
      if (isDrugDataAlreadyReleasedError(err)) {
        toast.error(
          '게시된 기준월은 초기화할 수 없습니다. 게시를 취소한 뒤 초기화하세요.',
        );
      } else if (isDrugDataProcessRunningError(err)) {
        toast.warning(
          '다른 생성 단계나 수집 배치가 실행 중입니다. 잠시 후 다시 시도하세요.',
        );
      } else {
        handleApiError(err, toast);
        toast.warning(
          '완료 상태가 이미 초기화됐을 수 있습니다. 같은 대상으로 다시 초기화하면 남은 데이터를 마저 정리합니다.',
        );
      }
      return null;
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <h1 className="text-2xl font-semibold text-foreground">
        의약품 정보 생성
      </h1>

      {/* 1) 년월 — 분리. 우측에 원천 데이터 수집(테이블 선택) 버튼 */}
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex flex-wrap items-center gap-5">
          <span className="text-xl font-semibold text-foreground">
            의약품 정보 년 월
          </span>
          <MonthPicker
            id="drug-data-ym"
            value={baseYm}
            onChange={handleBaseYmChange}
            className="h-10 w-[140px]"
          />
        </div>
        <MenuWriteGate>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-10 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              disabled={pipelineBusy}
              onClick={() => setResetModalOpen(true)}
            >
              {resetDrugData.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RotateCcw className="size-4" />
              )}
              데이터 초기화
            </Button>
            <Button
              type="button"
              className="ruda-button h-10"
              disabled={pipelineBusy}
              onClick={() => setTableSelectOpen(true)}
            >
              {runBatch.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <DownloadCloud className="size-4" />
              )}
              원천 데이터 수집
            </Button>
          </div>
        </MenuWriteGate>
      </div>

      {/* 2) 상태·버튼 카드 — 분리 */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,14fr)_minmax(0,10fr)]">
        <div className="rounded-[15px] border-[1.5px] border-[#6d6d6d] bg-white p-5">
          <div className="flex flex-wrap items-stretch justify-between gap-4">
            {SYNC_COLUMNS.map((col, index) => (
              <React.Fragment key={col.id}>
                <SyncServiceColumn
                  title={col.title}
                  status={
                    syncServiceUi[col.id]?.status ?? WORKFLOW_STATUS.PENDING
                  }
                  timeLabel={syncServiceUi[col.id]?.timeLabel ?? '—'}
                  showUpdate={col.showUpdate}
                  showUpload={col.showUpload}
                  onUpdate={
                    col.id === 'priceService'
                      ? () => void handlePriceServiceUpdate()
                      : col.id === 'mapping'
                        ? () => void handleMappingUpdate()
                        : undefined
                  }
                  onUpload={
                    col.id === 'dur'
                      ? handleDurUpload
                      : col.id === 'mapping'
                        ? handleMappingUpload
                        : undefined
                  }
                  updateDisabled={
                    pipelineBusy ||
                    syncServiceUi[col.id]?.status === WORKFLOW_STATUS.SUCCESS
                  }
                  uploadDisabled={
                    pipelineBusy ||
                    syncServiceUi[col.id]?.status === WORKFLOW_STATUS.SUCCESS
                  }
                  updateLoading={
                    col.id === 'priceService'
                      ? isFlagPending('batchFlag') || runBatch.isPending
                      : col.id === 'mapping'
                        ? isFlagPending('codeMappingFlag') ||
                          createCodeMapping.isPending
                        : false
                  }
                  uploadLoading={
                    col.id === 'dur'
                      ? isFlagPending('durFlag') ||
                        processHiraNonBenefit.isPending
                      : col.id === 'mapping'
                        ? isFlagPending('codeMappingFlag') ||
                          createCodeMapping.isPending
                        : false
                  }
                />
                {index < SYNC_COLUMNS.length - 1 ? (
                  <Separator
                    orientation="vertical"
                    className="hidden min-h-[160px] self-stretch xl:block"
                  />
                ) : null}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {workflowCardsWithUi.map((card) => (
            <ActionWorkflowCard
              key={card.id}
              title={card.title}
              actionLabel={card.actionLabel}
              status={card.status}
              timeLabel={card.timeLabel}
              borderActive={card.borderActive}
              actionDisabled={
                card.status === WORKFLOW_STATUS.SUCCESS ||
                (card.id === 'baseCreate'
                  ? !leftPrerequisitesSatisfied ||
                    flagsLoading ||
                    flagsError ||
                    pipelineBusy
                  : card.id === 'manual'
                    ? !manualInputEnabled ||
                      flagsLoading ||
                      flagsError ||
                      setDrugDataFlags.isPending ||
                      pipelineBusy
                    : !drugUpdateEnabled ||
                      flagsLoading ||
                      flagsError ||
                      pipelineBusy)
              }
              actionLoading={
                card.id === 'baseCreate'
                  ? isFlagPending('mergeFlag') || mergeHira.isPending
                  : card.id === 'manual'
                    ? setDrugDataFlags.isPending
                    : isFlagPending('updateFlag') || updateDrugInfo.isPending
              }
              onAction={
                card.id === 'baseCreate'
                  ? () => void handleBaseCreate()
                  : card.id === 'manual'
                    ? () => void handleManualInputComplete()
                    : () => setUpdateConfirmOpen(true)
              }
            />
          ))}
        </div>
      </div>

      {/* 확인 필요 · 공개 상태 바 */}
      <div className="flex flex-wrap items-center justify-end gap-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#232323]">
            확인 필요 의약품({missingCount !== null ? missingCount : '—'})
          </span>
          <Button
            type="button"
            size="sm"
            className="ruda-button"
            onClick={() => setConfirmModalOpen(true)}
          >
            보기
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="text-sm font-medium"
            style={{
              color:
                releaseWorkflowStatus === WORKFLOW_STATUS.SUCCESS
                  ? BRAND_PRIMARY
                  : '#232323',
            }}
          >
            의약품 정보
          </span>
          <WorkflowStatusIcon status={releaseWorkflowStatus} />
          <div className="w-40">
            <TimestampPill
              muted={
                releaseTimeLabel === '—' ||
                releaseWorkflowStatus === WORKFLOW_STATUS.PENDING
              }
              status={releaseWorkflowStatus}
            >
              {releaseTimeLabel}
            </TimestampPill>
          </div>
          <MenuWriteGate>
            <Button
              type="button"
              className="ruda-button min-w-[120px]"
              disabled={
                releaseDrugData.isPending ||
                Boolean(releaseStatus?.released) ||
                releaseStatus?.releasable === false
              }
              onClick={() => setPublishModalOpen(true)}
            >
              {releaseStatus?.released ? '공개 완료' : '의약품 정보 게시'}
            </Button>
            {releaseStatus?.released ? (
              <Button
                type="button"
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={cancelRelease.isPending}
                onClick={() => void handleCancelRelease()}
              >
                게시 취소
              </Button>
            ) : null}
          </MenuWriteGate>
        </div>
      </div>

      <Separator />

      {/* 3) 검색 필터 — 분리 */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#232323]">
              정보 구분
            </span>
            <Select
              value={infoCategory}
              onValueChange={(v) => {
                setInfoCategory(v);
                applySearch({
                  dataSourceType: v !== 'all' ? v : undefined,
                });
              }}
            >
              <SelectTrigger className="w-[200px] bg-white sm:w-[232px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INFO_CATEGORY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={underContractOnly}
              onCheckedChange={(v) => {
                const checked = v === true;
                setUnderContractOnly(checked);
                applySearch({
                  contractStatus: checked ? 'ACTIVE' : undefined,
                });
              }}
            />
            거래 중 제약사 의약품만 보기
          </label>
        </div>

        <form
          className="flex flex-wrap items-center gap-5"
          onSubmit={handleSearch}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#232323]">
              정보 항목
            </span>
            <Select
              value={infoField}
              onValueChange={(v) => {
                setInfoField(v);
                if (v === 'all') setSearchText('');
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px] bg-white sm:w-[232px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INFO_FIELD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="검색"
            disabled={infoField === 'all'}
            clearable={false}
            className="w-full sm:w-80"
          />
          <Button
            type="submit"
            className="ruda-button"
            disabled={infoField === 'all' || isFetching}
          >
            검색
          </Button>
        </form>
      </div>

      {/* 테이블 */}
      {isLoading ? (
        <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
      ) : rows.length === 0 ? (
        <EmptyState description="조회된 의약품이 없습니다." />
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#eae9ff] hover:bg-[#eae9ff]">
                <TableHead className="text-[#464f60]">의약품 코드</TableHead>
                <TableHead className="text-[#464f60]">품목 기준 코드</TableHead>
                <TableHead className="text-[#464f60]">
                  제약사 약품 코드
                </TableHead>
                <TableHead className="text-[#464f60]">제약사명</TableHead>
                <TableHead className="text-[#464f60]">의약품명</TableHead>
                <TableHead className="text-[#464f60]">주성분코드</TableHead>
                <TableHead className="text-[#464f60]">약가 - 심평원</TableHead>
                <TableHead className="text-[#464f60]">급여상태</TableHead>
                <TableHead className="text-[#464f60]">정보 구분</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.refYm}-${row.drugCode}`}>
                  <TableCell>{row.drugCode}</TableCell>
                  <TableCell>{row.drugCodeMfds ?? '-'}</TableCell>
                  <TableCell>{row.drugCodePharma ?? '-'}</TableCell>
                  <TableCell>{row.pharmaCompanyName ?? '-'}</TableCell>
                  <TableCell>{row.drugName}</TableCell>
                  <TableCell>{row.mainIngredientCode ?? '-'}</TableCell>
                  <TableCell>
                    {row.priceHira != null
                      ? row.priceHira.toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>{row.reimbursementType ?? '-'}</TableCell>
                  <TableCell>{row.dataSourceType ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="p-3">
            <SimplePagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>
      )}

      <ConfirmNeededDrugsModal
        open={confirmModalOpen}
        baseYm={baseYm}
        onClose={() => {
          setConfirmModalOpen(false);
          void refreshMissingCount();
        }}
        onValidationRefresh={() => void refreshMissingCount()}
      />
      <DrugInfoPublishScheduleModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={handleRelease}
      />
      <DrugInfoUpdateConfirmModal
        open={updateConfirmOpen}
        onClose={() => setUpdateConfirmOpen(false)}
        onConfirm={handleUpdateDrugInfo}
      />
      <DataCollectionTableSelectModal
        open={tableSelectOpen}
        onClose={() => setTableSelectOpen(false)}
        onConfirm={(tables) => void handleTableCollection(tables)}
      />
      <DrugDataResetModal
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        baseYm={baseYm}
        released={Boolean(releaseStatus?.released)}
        loading={resetDrugData.isPending}
        onConfirm={handleReset}
        onCancelRelease={() => void handleCancelRelease()}
      />
    </div>
  );
};
